import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminService from '../../../services/adminService';
import AILeadRankingService from '../../../services/aiLeadRankingService';
import { PageHeader } from '../../../components/ui';
import { useAuth } from '../../../context/AuthContext';

const toName = (s) => {
  const pi = s?.personal_info || {};
  const topFirst = s?.first_name || '';
  const topLast = s?.last_name || '';
  const topFull = [topFirst, topLast].filter(Boolean).join(' ').trim();
  return (
    pi.fullName || pi.name || pi.firstName || topFull || ''
  );
};
const toEmail = (s) => s?.personal_info?.email || s?.email || '';
const toPhone = (s) => s?.personal_info?.phone || s?.phone || '';
const toCountry = (s) => s?.country_selection?.country || s?.country || '';
const toPathway = (s) => s?.selected_pathway || s?.country_selection?.selectedPathway || '';
const toFirst = (s) => s?.first_name || s?.personal_info?.firstName || '';
const toLast = (s) => s?.last_name || s?.personal_info?.lastName || '';
const toUpdated = (s) => s?.updated_at || '';

const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (/[",\n]/.test(str)) return '"' + str.replace(/"/g, '""') + '"';
  return str;
};

const maskEmail = (email) => {
  if (!email) return '';
  const [userPart = '', domain = ''] = String(email).split('@');
  if (!domain) return `${userPart.slice(0, 1)}***`;
  const prefix = userPart ? `${userPart.slice(0, 1)}***` : '***';
  return `${prefix}@${domain}`;
};

const maskPhone = (phone) => {
  if (!phone) return '';
  const digits = String(phone).replace(/\s+/g, '');
  if (digits.length <= 4) return '*'.repeat(digits.length);
  const tail = digits.slice(-2);
  return `${'*'.repeat(digits.length - 2)}${tail}`;
};

const LeadsManager = () => {
  const { role, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [updating, setUpdating] = useState(null);
  const [rankingMap, setRankingMap] = useState({});
  const [loadingRankings, setLoadingRankings] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [includePII, setIncludePII] = useState(false);

  const canElevatePII = ['admin', 'manager'].includes(role);
  const showPII = canElevatePII && includePII;

  useEffect(() => {
    if (!canElevatePII && includePII) {
      setIncludePII(false);
    }
  }, [canElevatePII, includePII]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      setError(null);
      const { rows: data, count } = await AdminService.getWizardSubmissionsPaged({
        page,
        pageSize,
        status: statusFilter,
        dateFrom,
        dateTo,
        search,
        sortBy,
        sortDir
      });
      setRows(Array.isArray(data) ? data : []);
      setTotalCount(count || 0);
    } catch (e) {
      console.error('Leads load error', e);
      setError('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []);
  useEffect(() => { fetchLeads(); }, [statusFilter, dateFrom, dateTo, sortBy, sortDir, page, pageSize]);

  useEffect(() => {
    const rank = async () => {
      try {
        if (!rows.length) {
          setRankingMap({});
          return;
        }
        setLoadingRankings(true);
        const map = {};
        const missing = [];
        rows.forEach((r) => {
          const score = r.lead_score ?? null;
          const priority = r.lead_priority ?? null;
          if (score === null || priority === null) {
            missing.push(r);
          } else {
            map[r.id] = { score, priority };
          }
        });
        if (missing.length) {
          const rankings = await AILeadRankingService.batchCalculateRankings(missing);
          rankings.forEach((rk) => {
            map[rk.submissionId] = {
              score: rk.priorityScore?.score || 0,
              priority: rk.priorityLevel || 'UNKNOWN'
            };
            AdminService.upsertLeadRanking(rk.submissionId, {
              score: rk.priorityScore?.score || 0,
              priority: rk.priorityLevel || 'UNKNOWN'
            }).catch(() => { });
          });
        }
        setRankingMap(map);
      } catch (e) {
        console.error('Ranking calc error', e);
      } finally {
        setLoadingRankings(false);
      }
    };
    rank();
  }, [rows]);

  const statuses = useMemo(() => {
    const base = new Set(['submitted', 'reviewed', 'contacted', 'converted', 'closed']);
    rows.forEach((r) => r.status && base.add(String(r.status)));
    return Array.from(base);
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows.slice();
    if (statusFilter !== 'all') list = list.filter((r) => String(r.status) === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        return (
          toName(r).toLowerCase().includes(q) ||
          toEmail(r).toLowerCase().includes(q) ||
          toPhone(r).toLowerCase().includes(q) ||
          toCountry(r).toLowerCase().includes(q) ||
          toPathway(r).toLowerCase().includes(q)
        );
      });
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      list = list.filter((r) => new Date(r.created_at) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter((r) => new Date(r.created_at) <= to);
    }
    list.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortBy === 'created_at') return (new Date(a.created_at) - new Date(b.created_at)) * dir;
      if (sortBy === 'updated_at') return (new Date(a.updated_at || 0) - new Date(b.updated_at || 0)) * dir;
      if (sortBy === 'status') return String(a.status).localeCompare(String(b.status)) * dir;
      if (sortBy === 'country') return toCountry(a).localeCompare(toCountry(b)) * dir;
      if (sortBy === 'pathway') return toPathway(a).localeCompare(toPathway(b)) * dir;
      if (sortBy === 'score') {
        const sa = rankingMap[a.id]?.score || 0;
        const sb = rankingMap[b.id]?.score || 0;
        return (sa - sb) * dir;
      }
      if (sortBy === 'priority') {
        const pa = rankingMap[a.id]?.priority || '';
        const pb = rankingMap[b.id]?.priority || '';
        return pa.localeCompare(pb) * dir;
      }
      return 0;
    });
    return list;
  }, [rows, statusFilter, search, sortBy, sortDir, dateFrom, dateTo, rankingMap]);

  const total = totalCount || filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const exportCSV = () => {
    const headers = ['ID', 'Created At', 'Updated At', 'First Name', 'Last Name', 'Full Name'];
    if (showPII) {
      headers.push('Email', 'Phone');
    } else {
      headers.push('Email (redacted)', 'Phone (redacted)');
    }
    headers.push('Country', 'Pathway', 'Lead Score', 'Priority', 'Status', 'Assigned To');
    const lines = [headers.join(',')].concat(
      filtered.map((r) => [
        csvEscape(r.id),
        csvEscape(r.created_at),
        csvEscape(toUpdated(r)),
        csvEscape(toFirst(r)),
        csvEscape(toLast(r)),
        csvEscape(toName(r)),
        csvEscape(showPII ? toEmail(r) : maskEmail(toEmail(r))),
        csvEscape(showPII ? toPhone(r) : maskPhone(toPhone(r))),
        csvEscape(toCountry(r)),
        csvEscape(toPathway(r)),
        csvEscape(rankingMap[r.id]?.score ?? ''),
        csvEscape(rankingMap[r.id]?.priority ?? ''),
        csvEscape(r.status || ''),
        csvEscape(r.assigned_to || '')
      ].join(','))
    );
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const quickUpdate = async (row, changes) => {
    const id = row.id;
    try {
      setUpdating(id);
      const changeEntries = [];
      if (changes.status) {
        await AdminService.updateWizardSubmissionStatus(id, changes.status);
        changeEntries.push({ field: 'status', from: row.status || null, to: changes.status });
      }
      // refresh
      await fetchLeads();
      if (changeEntries.length) {
        await AdminService.recordAuditLog('wizard_submission_update', {
          entity: 'wizard_submission',
          entityId: id,
          summary: changeEntries.map((c) => `${c.field}: ${c.to ?? ''}`).join('; '),
          metadata: { changes: changeEntries },
          performedBy: user?.email || user?.id || null
        });
      }
    } catch (e) {
      console.error('Quick update error', e);
      alert('Failed to update lead');
    } finally {
      setUpdating(null);
    }
  };

  const changeSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('desc'); }
  };

  const priorityBadgeClass = (priority) => {
    const p = String(priority || '').toUpperCase();
    if (p.startsWith('HIGH')) return 'priority-high';
    if (p.includes('MEDIUM')) return 'priority-medium';
    if (p.includes('LOW')) return 'priority-low';
    return 'priority-low';
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-page text-white font-mono min-h-screen bg-gray-900">
      {/* Header */}
      <PageHeader
        darkMode={true}
        title="Leads Manager"
        subtitle="Manage incoming wizard submissions and qualify leads"
      />

      {/* Error */}
      {error && (
        <div className="admin-alert admin-alert-error mb-4">
          <p>{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="admin-card bg-gray-800 border-gray-700 mb-6">
        <div className="admin-card-content">
          <div className="admin-actions mb-4">
            <button onClick={exportCSV} className="btn-sm">📊 Export CSV</button>
            {canElevatePII && (
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={includePII}
                  onChange={(e) => setIncludePII(e.target.checked)}
                  className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
                />
                <span>🔒 Include PII</span>
              </label>
            )}
          </div>

          <div className="admin-grid-2 gap-4">
            <input
              placeholder="🔍 Search name, email, phone, country, pathway"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="admin-input bg-gray-700 border-gray-600 text-white placeholder-gray-400"
            />
            <div className="admin-grid-2 gap-2">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="admin-input bg-gray-700 border-gray-600 text-white"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="admin-input bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>

          <div className="admin-grid-2 gap-4 mt-4">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="admin-select bg-gray-700 border-gray-600 text-white">
              <option value="all">All statuses</option>
              {statuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value) || 20); setPage(1); }} className="admin-select bg-gray-700 border-gray-600 text-white">
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Desktop table */}
      <div className="admin-card table-desktop-only bg-gray-800 border-gray-700">
        <div className="admin-card-content p-0">
          <div className="overflow-x-auto">
            <table className="admin-table text-white w-full">
              <thead>
                <tr className="text-left border-b border-gray-700 bg-gray-900/50">
                  <th onClick={() => changeSort('country')} className="cursor-pointer p-3">Name</th>
                  <th className="p-3">Email{showPII ? '' : ' (redacted)'}</th>
                  <th className="p-3">Phone{showPII ? '' : ' (redacted)'}</th>
                  <th onClick={() => changeSort('country')} className="cursor-pointer p-3">Country</th>
                  <th onClick={() => changeSort('pathway')} className="cursor-pointer p-3">Pathway</th>
                  <th onClick={() => changeSort('score')} className="cursor-pointer p-3">Score</th>
                  <th onClick={() => changeSort('priority')} className="cursor-pointer p-3">Priority</th>
                  <th onClick={() => changeSort('status')} className="cursor-pointer p-3">Status</th>
                  <th className="p-3">Assigned</th>
                  <th className="p-3">CV</th>
                  <th onClick={() => changeSort('created_at')} className="cursor-pointer p-3">Created</th>
                  <th onClick={() => changeSort('updated_at')} className="cursor-pointer p-3">Updated</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {paged.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-700/50">
                    <td className="font-medium p-3">{toName(r) || r.id}</td>
                    <td className="p-3">{showPII ? (toEmail(r) || '-') : (maskEmail(toEmail(r)) || '—')}</td>
                    <td className="p-3">{showPII ? (toPhone(r) || '-') : (maskPhone(toPhone(r)) || '—')}</td>
                    <td className="p-3">{toCountry(r)}</td>
                    <td className="p-3">{toPathway(r)}</td>
                    <td className="font-medium p-3">{loadingRankings ? '…' : (rankingMap[r.id]?.score ?? '-')}</td>
                    <td className="p-3">
                      <span className={`status-badge ${rankingMap[r.id]?.priority?.startsWith('HIGH') ? 'priority-high' : rankingMap[r.id]?.priority?.includes('MEDIUM') ? 'priority-medium' : rankingMap[r.id]?.priority?.includes('LOW') ? 'priority-low' : 'priority-low'}`}>
                        {loadingRankings ? '…' : (rankingMap[r.id]?.priority ?? '-')}
                      </span>
                    </td>
                    <td className="p-3">
                      <select
                        disabled={updating === r.id}
                        value={r.status || 'new'}
                        onChange={(e) => quickUpdate(r, { status: e.target.value })}
                        className="admin-select bg-gray-700 border-gray-600 text-white text-xs py-1"
                      >
                        {statuses.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="text-gray-400 p-3">{r.assigned_to || '-'}</td>
                    <td className="text-gray-400 p-3">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="text-gray-400 p-3">{toUpdated(r) ? new Date(toUpdated(r)).toLocaleDateString() : '-'}</td>
                    <td className="p-3">
                      {r.cv_data?.publicUrl || r.cv_data?.url ? (
                        <a
                          href={r.cv_data.publicUrl || r.cv_data.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                          title="View CV"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </a>
                      ) : (
                        <span className="text-gray-600">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      <Link
                        to={`/admin/submissions/${r.id}`}
                        className="btn-primary-sm"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
                {paged.length === 0 && (
                  <tr>
                    <td colSpan={12} className="text-center py-8 text-gray-400">No leads found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile list */}
      <div className="table-mobile-only admin-grid-1 mt-4">
        {paged.map((r) => (
          <div key={r.id} className="admin-card bg-gray-800 border-gray-700">
            <div className="admin-card-content">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="font-semibold text-lg text-white">{toName(r) || r.id}</div>
                  <div className="text-sm text-gray-400 mt-1">Created: {new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <span className={`status-badge ${rankingMap[r.id]?.priority?.startsWith('HIGH') ? 'priority-high' : rankingMap[r.id]?.priority?.includes('MEDIUM') ? 'priority-medium' : rankingMap[r.id]?.priority?.includes('LOW') ? 'priority-low' : 'priority-low'}`}>
                  {loadingRankings ? '…' : (rankingMap[r.id]?.priority ?? '-')}
                </span>
              </div>

              <div className="admin-grid-2 gap-3 mb-4 text-sm">
                <div>
                  <span className="text-gray-400">Score:</span>
                  <span className="font-medium ml-2 text-white">{loadingRankings ? '…' : (rankingMap[r.id]?.score ?? '-')}</span>
                </div>
                <div>
                  <span className="text-gray-400">Country:</span>
                  <span className="font-medium ml-2 text-white">{toCountry(r)}</span>
                </div>
                <div className="col-span-full">
                  <span className="text-gray-400">Email:</span>
                  <span className="font-medium ml-2 break-all text-white">{showPII ? (toEmail(r) || '-') : (maskEmail(toEmail(r)) || '—')}</span>
                </div>
                <div className="col-span-full">
                  <span className="text-gray-400">Phone:</span>
                  <span className="font-medium ml-2 text-white">{showPII ? (toPhone(r) || '-') : (maskPhone(toPhone(r)) || '—')}</span>
                </div>
              </div>

              <div className="space-y-3">
                <select
                  disabled={updating === r.id}
                  value={r.status || 'new'}
                  onChange={(e) => quickUpdate(r, { status: e.target.value })}
                  className="admin-select bg-gray-700 border-gray-600 text-white w-full"
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <div className="admin-actions flex gap-2">
                  <Link
                    to={`/admin/submissions/${r.id}`}
                    className="btn-primary-sm flex-1 text-center"
                  >
                    View Details
                  </Link>
                  {r.cv_data?.publicUrl || r.cv_data?.url ? (
                    <a
                      href={r.cv_data.publicUrl || r.cv_data.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary-sm flex-shrink-0 flex items-center justify-center px-3"
                      title="View CV"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ))}
        {paged.length === 0 && (
          <div className="text-center py-12 text-gray-400">No leads found.</div>
        )}
      </div>

      {/* Pagination */}
      <div className="admin-pagination mt-6 flex justify-between items-center text-gray-400">
        <div>
          Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
        </div>
        <div className="admin-pagination-buttons flex gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage(1)}
            className="btn-sm disabled:opacity-50 hover:bg-gray-700"
          >
            First
          </button>
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            className="btn-sm disabled:opacity-50 hover:bg-gray-700"
          >
            Prev
          </button>
          <span className="font-medium flex items-center px-2">Page {page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            className="btn-sm disabled:opacity-50 hover:bg-gray-700"
          >
            Next
          </button>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(totalPages)}
            className="btn-sm disabled:opacity-50 hover:bg-gray-700"
          >
            Last
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadsManager;
